import React, { useState } from "react";
import { Form, Button, Card, Alert, FormCheck } from "react-bootstrap";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import BasicInformation from "../components/form/BasicInformation";
import ContactInformation from "../components/form/ContactInformation";
import AddressInformation from "../components/form/AddressInformation";
import EducationInformation from "../components/form/EducationInformation";
import styled from "styled-components";
import { createApplication } from "../api/services/ApplicationService";
import { useLocation, useNavigate } from "react-router-dom";

const StyledCard = styled(Card)`
  border: none;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  overflow: hidden;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-2px);
  }
`;

const StyledCardHeader = styled(Card.Header)`
  background: linear-gradient(135deg, #4b6cb7 0%, #182848 100%);
  color: #fff;
  font-weight: 600;
  letter-spacing: 0.5px;
  padding: 1.25rem 1.5rem;
`;

const StyledForm = styled(Form)`
  .section-title {
    color: #4b6cb7;
    font-weight: 600;
    margin: 1.5rem 0 1rem;
    padding-bottom: 0.5rem;
    border-bottom: 2px solid #f0f2f5;
    position: relative;

    &:after {
      content: "";
      position: absolute;
      left: 0;
      bottom: -2px;
      width: 60px;
      height: 2px;
      background: linear-gradient(90deg, #4b6cb7 0%, #182848 100%);
    }
  }

  .declaration-checkbox {
    margin: 1.5rem 0;
    padding: 1rem;
    background-color: #f8f9fa;
    border-radius: 8px;
    border-left: 4px solid #4b6cb7;

    .form-check-input {
      margin-top: 0.25rem;
    }

    .form-check-label {
      font-weight: 500;
    }
  }
`;

const StyledButton = styled(Button)`
  min-width: 120px;
  font-weight: 500;
  letter-spacing: 0.5px;
  transition: all 0.3s ease;

  &.btn-outline-secondary {
    &:hover {
      background-color: #f8f9fa;
    }
  }
`;

const StudentForm = ({ onSubmit, initialData = {} }) => {
  const schema = yup.object().shape({
    firstName: yup
      .string()
      .required("First name is required")
      .matches(/^[A-Za-z\s]+$/, "First name must contain only letters")
      .max(100, "First name cannot exceed 100 characters"),
    middleName: yup
      .string()
      .matches(/^[A-Za-z\s]*$/, "Middle name must contain only letters")
      .max(10, "Middle Name cannot exceed 10 characters"),
    lastName: yup
      .string()
      .required("Last name is required")
      .matches(/^[A-Za-z\s]+$/, "Last name must contain only letters")
      .max(100, "Last name cannot exceed 100 characters"),
    birthDate: yup
      .date()
      .typeError("Please enter a valid date")
      .required("Birth date is required")
      .max(new Date(), "Date cannot be in the future")
      .min(new Date("1900-01-01"), "Date must be after 1900"),
    gender: yup
      .string()
      .required("Gender is required")
      .oneOf(
        ["Male", "Female", "Other"],
        "Gender must be Male, Female, or Other"
      ),
    phone: yup
      .string()
      .required("Phone number is required")
      .matches(/^[6-9]\d{9}$/, "Must be a valid 10-digit phone number"),
    email: yup
      .string()
      .required("Email is required")
      .email("Must be a valid email address"),
    address: yup.object().shape({
      street: yup.string().required("Street address is required"),
      city: yup.string().required("City is required"),
      district: yup.string().required("District is required"),
      state: yup.string().required("State is required"),
      postalCode: yup
        .string()
        .required("Postal code is required")
        .matches(/^[1-9][0-9]{5}$/, "Must be a valid 6-digit PIN code"),
      country: yup.string().default("India"),
    }),
    educationQualification: yup
      .string()
      .required("Education qualification is required")
      .max(100, "Education qualification cannot exceed 100 characters"),
    declaration: yup
      .boolean()
      .oneOf([true], "You must accept the declaration to proceed")
      .required("You must accept the declaration to proceed"),
  });

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      ...initialData,
      address: {
        country: "India",
        ...initialData.address,
      },
      // status: initialData.status || "active",
      declaration: false,
    },
  });

  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  const onFormSubmit = async (data) => {
    try {
      setSubmitError(null);
      setSubmitSuccess(false);

      const paymentResponse = await createApplication(data);

      console.log("Payment Response:", paymentResponse);
      if (paymentResponse.sessionId) {
        navigate("/payment", {
          state: {
            sessionId: paymentResponse.sessionId,
          },
        });
      } else {
        throw new Error("Failed to create payment session");
      }
    } catch (error) {
      if (error.response) {
        const res = error.response.data;

        //  1. Check for MongoDB duplicate key error (E11000) first
        if (typeof res.details === "string" && res.details.includes("E11000")) {
          const emailMatch = res.details.match(/email.+?"([^"]+)"/i);
          if (emailMatch) {
            setError("email", {
              type: "server",
              message: "This email is already registered. Please use another email address.",
            });
          }

          setSubmitError("Please correct the highlighted fields.");
        }

        // "Student validation failed: middleName: Middle name cannot exceed 10 characters"
        else if (typeof res.details === "string" && res.details.includes("Student validation failed")) {
          const match = res.details.match(/([a-zA-Z]+): ([^:]+)$/); // match "middleName: Message"
          if (match) {
            const [, field, message] = match;

            setError(field, {
              type: "server",
              message,
            });

            setSubmitError("Please correct the highlighted fields.");
          }
        }

        //  2. Field-level backend validation errors
        else if (res.errors && typeof res.errors === "object") {
          Object.entries(res.errors).forEach(([field, message]) => {
            setError(field, {
              type: "server",
              message,
            });
          });

          setSubmitError("Please correct the highlighted fields.");
        }

        //  3. General message
        else if (res.message) {
          setSubmitError(res.message);
        } else {
          setSubmitError("Something went wrong. Please try again.");
        }
      } else {
        setSubmitError(error.message || "Failed to submit form");
      }
    }
  };

  return (
    <StyledCard className="mb-5">
      <StyledCardHeader as="h5">Student Registration IBM Global Entrance Test</StyledCardHeader>
      <Card.Body>
        {submitError && (
          <Alert
            variant="danger"
            dismissible
            onClose={() => setSubmitError(null)}
            className="border-0 shadow-sm"
          >
            <i className="bi bi-exclamation-circle me-2"></i>
            {submitError}
          </Alert>
        )}
        {submitSuccess && (
          <Alert
            variant="success"
            dismissible
            onClose={() => setSubmitSuccess(false)}
            className="border-0 shadow-sm"
          >
            <i className="bi bi-check-circle me-2"></i>
            Student record {initialData._id ? "updated" : "saved"} successfully!
          </Alert>
        )}

        <StyledForm onSubmit={handleSubmit(onFormSubmit)}>
          <BasicInformation register={register} errors={errors} />
          <ContactInformation register={register} errors={errors} />
          <AddressInformation register={register} errors={errors} />
          <EducationInformation register={register} errors={errors} />

          {/* Declaration & Verification Section */}
          <div className="declaration-checkbox">
            <Form.Check
              type="checkbox"
              id="declaration"
              label="I hereby declare that all the information provided is true to the best of my knowledge."
              {...register("declaration")}
              isInvalid={!!errors.declaration}
            />
            {errors.declaration && (
              <Form.Control.Feedback type="invalid" className="d-block">
                <i className="bi bi-exclamation-circle me-2"></i>
                {errors.declaration.message}
              </Form.Control.Feedback>
            )}
          </div>

          <div className="d-flex justify-content-end mt-4 gap-3">
            <StyledButton
              variant="outline-secondary"
              type="button"
              onClick={() => reset()}
              disabled={isSubmitting}
            >
              <i className="bi bi-arrow-counterclockwise me-2"></i>
              Reset
            </StyledButton>
            <StyledButton
              variant="primary"
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Processing...
                </>
              ) : (
                <>
                  <i
                    className={`bi ${initialData._id ? "bi-pencil" : "bi-save"
                      } me-2`}
                  ></i>
                  {initialData._id ? "Update" : "Submit"}
                </>
              )}
            </StyledButton>
          </div>
        </StyledForm>
      </Card.Body>
    </StyledCard>
  );
};

export default StudentForm;
